import { observer } from 'mobx-react';
import { FC, useEffect } from 'react';

import { useDependencies } from '@servicetitan/react-ioc';
import { TextField } from '@servicetitan/anvil2';

import { useFormInputs } from '../../../hooks/use-form-inputs';
import { LoadStatus } from '../../../../common/enums/load-status';
import { ManageTenantStore } from '../../../stores/manage-tenant.store';
import { NotificationsStore } from '../../../../common/stores/notifications.store';
import { ConfigurationWrapper } from '../../configuration-helpers/configuration-wrapper';
import { ActiveFieldsSection } from '../../configuration-helpers/active-fields-section';
import { OptInOutField } from '../../configuration-helpers/opt-in-out-field';
import { SectionsDivider } from '../../manage-partition/sections-divider';
import { WarmupDomainsField } from '../../configuration-helpers/warmup-domains-field';
import { DisabledFieldsSection } from '../../configuration-helpers/disabled-fields-section';

export const EmailDirectMail: FC = observer(() => {
    const [
        { tenant, tenantGenericConfigs, saveLoadStatus, formState, resetConfiguration },
        { notifyInSnackbar },
    ] = useDependencies(ManageTenantStore, NotificationsStore);

    const { getInputProps } = useFormInputs();

    const {
        emailScope,
        emailSenderDomain,
        directMailProvider,
        authPath,
        emailTemplateOptOut,
        warmupDomains,
    } = formState.$;

    useEffect(() => {
        if (saveLoadStatus === LoadStatus.Ok) {
            notifyInSnackbar({ title: 'Changes saved' });
        }
    }, [saveLoadStatus, notifyInSnackbar]);

    useEffect(
        () => () => {
            resetConfiguration();
        },
        [resetConfiguration],
    );

    return (
        <ConfigurationWrapper isLoaded={!!tenant}>
            <ActiveFieldsSection>
                <TextField label="Email Sender Domain" {...getInputProps(emailSenderDomain)} />
                <WarmupDomainsField
                    key={tenant?.internalId}
                    value={warmupDomains.value}
                    onChange={warmupDomains.onChange}
                    senderDomain={emailSenderDomain.value}
                />
                <TextField label="Email Scope" {...getInputProps(emailScope)} />
                <TextField label="Direct Mail Provider" {...getInputProps(directMailProvider)} />
                {tenant?.partnerDetail.configs.isPartnerLevelAuth === false && (
                    <TextField label="Auth Path URL" {...getInputProps(authPath)} />
                )}
                <OptInOutField field={emailTemplateOptOut} />
            </ActiveFieldsSection>
            <SectionsDivider showDivider={tenantGenericConfigs.size > 0} />
            <DisabledFieldsSection configs={tenantGenericConfigs} />
        </ConfigurationWrapper>
    );
});
